// Internal Dashboard — Command Center for RFQ Management (Live Data)
import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../../components/layout/Layout';
import { Shield, Clock, CheckCircle2, AlertTriangle, XCircle, Send, Filter, Activity, Zap, Target, RefreshCw, Save, Database, Mail, TrendingUp, AlertCircle, Download, Search, MessageSquare, UserCheck } from 'lucide-react';
import { calculateRFQScore, PriorityLevel, PRIORITY_STYLES } from '../../components/rfq/rfqScoringEngine';

import { BACKEND_URL } from '../../config/api';

// ─── TYPES ───────────────────────────────────────────────────────────────────

interface RFQRecord {
  id: number;
  referenceId: string;
  clientName: string;
  clientEmail: string;
  companyName: string;
  phone: string;
  service: string;
  location: string;
  parameters: string;
  timeline: string;
  notes: string;
  status: string;
  priority: string;
  assignedTo: string;
  internalNotes: string;
  formSummary: string;
  preferredResponseTime: string;
  createdAt: number;
}

function getRFQPriorityInfo(rfq: RFQRecord): { score: number; priority: PriorityLevel } {
  let params: any = {};
  try { params = JSON.parse(rfq.parameters || '{}'); } catch { params = {}; }
  const result = calculateRFQScore({
    service: rfq.service || '',
    location: rfq.location || '',
    timeline: rfq.timeline || params.timeline || '',
    h2sPresence: params.h2s || params.h2sPresence || 'no',
    wellheadPressure: params.wellheadPressure || params.pressure || '',
    sandProduction: params.sandProduction || params.sand || '',
    clientType: params.clientType || 'other',
    companyName: rfq.companyName || '',
  });
  return { score: result.score, priority: result.priority };
}

const PRIORITY_BADGE_STYLES: Record<PriorityLevel, { bg: string; text: string; border: string; label: string }> = {
  high: { bg: 'bg-red-500/10', text: 'text-red-300', border: 'border-red-500/30', label: 'HIGH' },
  medium: { bg: 'bg-amber-500/10', text: 'text-amber-300', border: 'border-amber-500/30', label: 'MED' },
  low: { bg: 'bg-green-500/10', text: 'text-green-300', border: 'border-green-500/30', label: 'LOW' },
};

const STATUS_OPTIONS = ['Submitted', 'Under Review', 'Proposal Sent', 'Approved', 'Rejected', 'Closed'];

const STATUS_STYLES: Record<string, { bg: string; text: string; border: string }> = {
  'Submitted': { bg: 'bg-blue-500/10', text: 'text-blue-300', border: 'border-blue-500/30' },
  'Under Review': { bg: 'bg-amber-500/10', text: 'text-amber-300', border: 'border-amber-500/30' },
  'Proposal Sent': { bg: 'bg-purple-500/10', text: 'text-purple-300', border: 'border-purple-500/30' },
  'Approved': { bg: 'bg-green-500/10', text: 'text-green-300', border: 'border-green-500/30' },
  'Rejected': { bg: 'bg-red-500/10', text: 'text-red-300', border: 'border-red-500/30' },
  'Closed': { bg: 'bg-slate-500/10', text: 'text-slate-400', border: 'border-slate-500/30' },
};

// ─── INTERNAL DASHBOARD ──────────────────────────────────────────────────────

export const InternalDashboard: React.FC = () => {
  const [rfqs, setRfqs] = useState<RFQRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingStatus, setEditingStatus] = useState('');
  const [saveMessage, setSaveMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [testEmailStatus, setTestEmailStatus] = useState<string | null>(null);
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [testEmailInput, setTestEmailInput] = useState('');
  const [internalTestStatus, setInternalTestStatus] = useState<string | null>(null);
  const [isSendingInternalTest, setIsSendingInternalTest] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [assignInput, setAssignInput] = useState<Record<number, string>>({});
  const [notesInput, setNotesInput] = useState<Record<number, string>>({});

  // Email validation
  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  // Send test email via server-side backend
  const sendTestEmail = async () => {
    if (!isValidEmail(testEmailInput)) return;
    setIsSendingTest(true);
    setTestEmailStatus(null);
    console.log('Sending test email...');
    try {
      const res = await fetch(`${BACKEND_URL}/api/public/test-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: testEmailInput }),
      });
      const data = await res.json();
      console.log('Response:', data);
      if (data.success) {
        setTestEmailStatus('✓ Email sent successfully');
      } else {
        console.error('Error:', data.error);
        setTestEmailStatus(`✗ ${data.error}`);
      }
    } catch (err: any) {
      console.error('Error:', err.message);
      setTestEmailStatus(`✗ Error: ${err.message}`);
    } finally {
      setIsSendingTest(false);
      setTimeout(() => setTestEmailStatus(null), 8000);
    }
  };

  // Send internal test notification to sales team
  const sendInternalTestNotification = async () => {
    setIsSendingInternalTest(true);
    setInternalTestStatus(null);
    console.log('[ADMIN] Sending internal test notification to sales@energiprogroup.com...');
    try {
      const res = await fetch(`${BACKEND_URL}/api/public/test-internal-notification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      console.log('[ADMIN] Internal test response:', data);
      if (data.success) {
        setInternalTestStatus(`✓ Internal notification sent to ${data.sentTo} (Test RFQ: ${data.rfqId})`);
      } else {
        console.error('[ADMIN] Internal test error:', data.error);
        setInternalTestStatus(`✗ Failed: ${data.error}`);
      }
    } catch (err: any) {
      console.error('[ADMIN] Internal test exception:', err.message);
      setInternalTestStatus(`✗ Error: ${err.message}`);
    } finally {
      setIsSendingInternalTest(false);
      setTimeout(() => setInternalTestStatus(null), 10000);
    }
  };

  // Fetch RFQs from database
  const fetchRFQs = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/public/rfqs`);
      if (!res.ok) throw new Error('Failed to fetch RFQs');
      const data = await res.json();
      setRfqs(data.data || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load RFQs');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load data on mount
  useEffect(() => {
    fetchRFQs();
  }, [fetchRFQs]);

  // Update status in database
  const handleStatusUpdate = async (id: number, newStatus: string) => {
    setIsSaving(true);
    setSaveMessage('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/public/rfq/${id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({ error: 'Failed' }));
        throw new Error(errData.error || 'Failed to update status');
      }
      // Update local state
      setRfqs(prev => prev.map(r => r.id === id ? { ...r, status: newStatus } : r));
      setEditingId(null);
      setSaveMessage(`Status updated to "${newStatus}" for RFQ #${id}`);
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (err: any) {
      setSaveMessage(`Error: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Filter & sort (high priority first)
  const filteredRFQs = rfqs
    .filter(r => statusFilter === 'all' || r.status === statusFilter)
    .filter(r => priorityFilter === 'all' || getRFQPriorityInfo(r).priority === priorityFilter)
    .filter(r => {
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      return (
        (r.referenceId || '').toLowerCase().includes(term) ||
        (r.clientName || '').toLowerCase().includes(term) ||
        (r.companyName || '').toLowerCase().includes(term) ||
        (r.clientEmail || '').toLowerCase().includes(term) ||
        (r.service || '').toLowerCase().includes(term)
      );
    })
    .map(r => ({ ...r, ...getRFQPriorityInfo(r) }))
    .sort((a, b) => {
      // Sort by priority first (high > medium > low), then by date
      const prioOrder: Record<string, number> = { high: 0, medium: 1, low: 2 };
      const prioDiff = (prioOrder[a.priority] || 1) - (prioOrder[b.priority] || 1);
      if (prioDiff !== 0) return prioDiff;
      return (b.createdAt || 0) - (a.createdAt || 0);
    });

  // CSV Export function
  const exportToCSV = () => {
    const headers = ['Reference ID', 'Client Name', 'Company', 'Email', 'Phone', 'Service', 'Location', 'Status', 'Priority', 'Assigned To', 'Timeline', 'Date'];
    const rows = rfqs.map(r => [
      r.referenceId, r.clientName, r.companyName, r.clientEmail, r.phone || '',
      r.service, r.location, r.status, r.priority || 'Standard', r.assignedTo || '',
      r.timeline || r.preferredResponseTime || '', formatDate(r.createdAt),
    ]);
    const csv = [headers.join(','), ...rows.map(row => row.map(v => `"${(v || '').replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `energipro-rfqs-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Update RFQ fields (assign, notes)
  const handleFieldUpdate = async (id: number, fields: Record<string, string>) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/public/rfq/${id}/update`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fields),
      });
      if (!res.ok) throw new Error('Failed');
      setRfqs(prev => prev.map(r => r.id === id ? { ...r, ...fields } : r));
      setSaveMessage(`Updated RFQ #${id}`);
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (err: any) {
      setSaveMessage(`Error: ${err.message}`);
    }
  };

  // KPIs
  const totalRFQs = rfqs.length;
  const submitted = rfqs.filter(r => r.status === 'Submitted').length;
  const underReview = rfqs.filter(r => r.status === 'Under Review').length;
  const closed = rfqs.filter(r => r.status === 'Closed' || r.status === 'Approved').length;

  const formatDate = (timestamp: number) => {
    if (!timestamp) return '—';
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <Layout>
      <div className="min-h-screen bg-[#0a0f1e] py-12">
        <div className="max-w-7xl mx-auto px-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="font-['Oswald'] text-3xl text-white uppercase tracking-wide">RFQ Command Center</h1>
              <p className="text-slate-400 text-sm mt-1">Live Database — Real-time RFQ Management</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={exportToCSV}
                className="flex items-center gap-2 px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-300 text-sm hover:bg-slate-700 transition-all"
              >
                <Download className="w-4 h-4" /> Export CSV
              </button>
              <button
                onClick={fetchRFQs}
                className="flex items-center gap-2 px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-300 text-sm hover:bg-slate-700 transition-all"
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} /> Refresh
              </button>
              <div className="flex items-center gap-2 px-3 py-2 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                <Shield className="w-4 h-4 text-orange-400" />
                <span className="text-orange-300 text-sm font-medium">Admin</span>
              </div>
            </div>
          </div>

          {/* ═══ EMAIL SYSTEM TEST CARD ═══ */}
          <div className="mb-6 bg-[#111827] border border-slate-700/50 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-green-500/10 rounded-lg">
                <Mail className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <h2 className="text-white font-bold text-lg">Email System Test</h2>
                <p className="text-slate-400 text-xs">Send a test email to verify the notification system is working</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="email"
                value={testEmailInput}
                onChange={e => setTestEmailInput(e.target.value)}
                placeholder="Enter email address"
                className="flex-1 max-w-md px-4 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-green-500 transition-colors"
              />
              <button
                onClick={sendTestEmail}
                disabled={isSendingTest || !isValidEmail(testEmailInput)}
                className="flex items-center gap-2 px-5 py-2.5 bg-green-600/20 border border-green-500/40 rounded-lg text-green-300 text-sm font-medium hover:bg-green-600/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className={`w-4 h-4 ${isSendingTest ? 'animate-pulse' : ''}`} />
                {isSendingTest ? 'Sending...' : 'Send Test Email'}
              </button>
            </div>
            {/* Result message */}
            {testEmailStatus && (
              <div className={`mt-4 p-3 rounded-lg border flex items-center gap-2 ${testEmailStatus.startsWith('✗') ? 'bg-red-500/10 border-red-500/30' : 'bg-green-500/10 border-green-500/30'}`}>
                {testEmailStatus.startsWith('✗') ? <AlertTriangle className="w-4 h-4 text-red-400" /> : <CheckCircle2 className="w-4 h-4 text-green-400" />}
                <span className={`text-sm ${testEmailStatus.startsWith('✗') ? 'text-red-300' : 'text-green-300'}`}>{testEmailStatus}</span>
              </div>
            )}

            {/* Internal Notification Test */}
            <div className="mt-6 pt-5 border-t border-slate-700/50">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-1.5 bg-orange-500/10 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-orange-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm">Internal Sales Notification Test</h3>
                  <p className="text-slate-400 text-xs">Sends a test RFQ alert to sales@energiprogroup.com</p>
                </div>
              </div>
              <button
                onClick={sendInternalTestNotification}
                disabled={isSendingInternalTest}
                className="flex items-center gap-2 px-5 py-2.5 bg-orange-600/20 border border-orange-500/40 rounded-lg text-orange-300 text-sm font-medium hover:bg-orange-600/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className={`w-4 h-4 ${isSendingInternalTest ? 'animate-pulse' : ''}`} />
                {isSendingInternalTest ? 'Sending...' : 'Send Internal Test Notification'}
              </button>
              {internalTestStatus && (
                <div className={`mt-3 p-3 rounded-lg border flex items-center gap-2 ${internalTestStatus.startsWith('✗') ? 'bg-red-500/10 border-red-500/30' : 'bg-green-500/10 border-green-500/30'}`}>
                  {internalTestStatus.startsWith('✗') ? <AlertTriangle className="w-4 h-4 text-red-400" /> : <CheckCircle2 className="w-4 h-4 text-green-400" />}
                  <span className={`text-sm ${internalTestStatus.startsWith('✗') ? 'text-red-300' : 'text-green-300'}`}>{internalTestStatus}</span>
                </div>
              )}
            </div>
          </div>

          {/* Save Notification */}
          {saveMessage && (
            <div className={`mb-4 p-3 rounded-lg border flex items-center gap-2 ${saveMessage.startsWith('Error') ? 'bg-red-500/10 border-red-500/30' : 'bg-green-500/10 border-green-500/30'}`}>
              {saveMessage.startsWith('Error') ? <AlertTriangle className="w-4 h-4 text-red-400" /> : <CheckCircle2 className="w-4 h-4 text-green-400" />}
              <span className={`text-sm ${saveMessage.startsWith('Error') ? 'text-red-300' : 'text-green-300'}`}>{saveMessage}</span>
            </div>
          )}

          {/* KPI Strip */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-[#111827] border border-slate-700/50 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 bg-blue-500/10 rounded-lg"><Database className="w-4 h-4 text-blue-400" /></div>
                <span className="text-slate-400 text-xs uppercase">Total RFQs</span>
              </div>
              <p className="text-3xl font-bold text-white font-['Oswald']">{totalRFQs}</p>
              <p className="text-slate-500 text-xs mt-1">All time</p>
            </div>
            <div className="bg-[#111827] border border-red-500/20 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 bg-red-500/10 rounded-lg"><TrendingUp className="w-4 h-4 text-red-400" /></div>
                <span className="text-slate-400 text-xs uppercase">High Priority</span>
              </div>
              <p className="text-3xl font-bold text-red-300 font-['Oswald']">{rfqs.filter(r => getRFQPriorityInfo(r).priority === 'high').length}</p>
              <p className="text-slate-500 text-xs mt-1">Score ≥ 80</p>
            </div>
            <div className="bg-[#111827] border border-blue-500/20 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 bg-blue-500/10 rounded-lg"><Send className="w-4 h-4 text-blue-400" /></div>
                <span className="text-slate-400 text-xs uppercase">Submitted</span>
              </div>
              <p className="text-3xl font-bold text-blue-300 font-['Oswald']">{submitted}</p>
              <p className="text-slate-500 text-xs mt-1">Awaiting review</p>
            </div>
            <div className="bg-[#111827] border border-amber-500/20 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 bg-amber-500/10 rounded-lg"><Clock className="w-4 h-4 text-amber-400" /></div>
                <span className="text-slate-400 text-xs uppercase">Under Review</span>
              </div>
              <p className="text-3xl font-bold text-amber-300 font-['Oswald']">{underReview}</p>
              <p className="text-slate-500 text-xs mt-1">In progress</p>
            </div>
            <div className="bg-[#111827] border border-green-500/20 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 bg-green-500/10 rounded-lg"><CheckCircle2 className="w-4 h-4 text-green-400" /></div>
                <span className="text-slate-400 text-xs uppercase">Completed</span>
              </div>
              <p className="text-3xl font-bold text-green-300 font-['Oswald']">{closed}</p>
              <p className="text-slate-500 text-xs mt-1">Approved or closed</p>
            </div>
          </div>

          {/* High Priority Alert Banner */}
          {rfqs.filter(r => getRFQPriorityInfo(r).priority === 'high' && (r.status === 'Submitted' || r.status === 'Under Review')).length > 0 && (
            <div className="mb-4 p-4 bg-red-500/5 border border-red-500/30 rounded-xl">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-400 animate-pulse" />
                <span className="text-red-300 text-sm font-bold">
                  ⚠️ {rfqs.filter(r => getRFQPriorityInfo(r).priority === 'high' && (r.status === 'Submitted' || r.status === 'Under Review')).length} High-Priority RFQ(s) require immediate action
                </span>
              </div>
            </div>
          )}

          {/* Status Filter Pills + Search */}
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <div className="flex items-center gap-2 flex-1 max-w-xs">
              <Search className="w-4 h-4 text-slate-500" />
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search RFQs..."
                className="flex-1 px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-white text-xs placeholder:text-slate-500 focus:outline-none focus:border-orange-500"
              />
            </div>
            <Filter className="w-4 h-4 text-slate-500" />
            <span className="text-slate-500 text-xs uppercase">Status:</span>
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${statusFilter === 'all' ? 'bg-orange-500/10 border-orange-500/40 text-orange-300' : 'bg-slate-800 border-slate-700 text-slate-400 hover:text-slate-300'}`}
            >
              All ({rfqs.length})
            </button>
            {STATUS_OPTIONS.map(s => {
              const count = rfqs.filter(r => r.status === s).length;
              return (
                <button
                  key={s}
                  onClick={() => setStatusFilter(statusFilter === s ? 'all' : s)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${statusFilter === s ? 'bg-orange-500/10 border-orange-500/40 text-orange-300' : 'bg-slate-800 border-slate-700 text-slate-400 hover:text-slate-300'}`}
                >
                  {s} ({count})
                </button>
              );
            })}
          </div>

          {/* Error State */}
          {error && (
            <div className="mb-6 p-6 bg-red-500/10 border border-red-500/30 rounded-xl text-center">
              <XCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
              <p className="text-red-300 font-medium">{error}</p>
              <button onClick={fetchRFQs} className="mt-3 px-4 py-2 bg-red-500/20 border border-red-500/30 rounded-lg text-red-300 text-sm hover:bg-red-500/30 transition-all">
                Try Again
              </button>
            </div>
          )}

          {/* Loading State */}
          {isLoading && !error && (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 text-orange-400 animate-spin mx-auto mb-3" />
              <p className="text-slate-400">Loading RFQs from database...</p>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && !error && rfqs.length === 0 && (
            <div className="text-center py-16 bg-[#111827] border border-slate-700/50 rounded-xl">
              <Database className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400 text-lg">No RFQs in database yet</p>
              <p className="text-slate-500 text-sm mt-1">Submit an RFQ from the Contact page to see it here</p>
            </div>
          )}

          {/* RFQ Table */}
          {!isLoading && !error && filteredRFQs.length > 0 && (
            <div className="bg-[#111827] border border-slate-700/50 rounded-xl overflow-hidden">
              <div className="p-5 border-b border-slate-700/50 flex items-center justify-between">
                <h2 className="text-white font-bold">RFQ Pipeline ({filteredRFQs.length})</h2>
                <div className="flex items-center gap-2 text-slate-500 text-xs">
                  <Activity className="w-3.5 h-3.5" /> Live Database
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-700/50 bg-slate-800/30">
                      <th className="text-left py-3 px-4 text-slate-400 text-xs uppercase font-semibold">Reference ID</th>
                      <th className="text-left py-3 px-4 text-slate-400 text-xs uppercase font-semibold">Priority</th>
                      <th className="text-left py-3 px-4 text-slate-400 text-xs uppercase font-semibold">Client</th>
                      <th className="text-left py-3 px-4 text-slate-400 text-xs uppercase font-semibold">Service</th>
                      <th className="text-left py-3 px-4 text-slate-400 text-xs uppercase font-semibold">Status</th>
                      <th className="text-left py-3 px-4 text-slate-400 text-xs uppercase font-semibold">Date</th>
                      <th className="text-left py-3 px-4 text-slate-400 text-xs uppercase font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRFQs.map(rfq => {
                      const style = STATUS_STYLES[rfq.status] || STATUS_STYLES['Submitted'];
                      const isEditing = editingId === rfq.id;
                      const prioInfo = getRFQPriorityInfo(rfq);
                      const prioBadge = PRIORITY_BADGE_STYLES[prioInfo.priority];
                      const isHigh = prioInfo.priority === 'high';
                      return (
                        <React.Fragment key={rfq.id}>
                        <tr className={`border-b border-slate-700/30 hover:bg-slate-800/30 transition-colors ${isHigh ? 'bg-red-500/5' : ''}`}>
                          <td className="py-3 px-4 text-orange-300 font-mono text-xs font-medium">{rfq.referenceId}</td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${prioBadge.bg} ${prioBadge.text} ${prioBadge.border}`}>
                                {prioBadge.label}
                              </span>
                              <span className={`text-[10px] font-mono font-bold ${prioInfo.score >= 80 ? 'text-red-400' : prioInfo.score >= 50 ? 'text-amber-400' : 'text-green-400'}`}>
                                {prioInfo.score}
                              </span>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <p className="text-white text-xs font-medium">{rfq.clientName || '—'}</p>
                            <p className="text-slate-500 text-[10px]">{rfq.companyName || rfq.clientEmail}</p>
                          </td>
                          <td className="py-3 px-4 text-white text-xs">{rfq.service || '—'}</td>
                          <td className="py-3 px-4">
                            {isEditing ? (
                              <select
                                value={editingStatus}
                                onChange={e => setEditingStatus(e.target.value)}
                                className="bg-slate-800 border border-slate-600 text-white text-xs rounded-lg px-2 py-1.5 focus:border-orange-500 focus:outline-none"
                              >
                                {STATUS_OPTIONS.map(s => (
                                  <option key={s} value={s}>{s}</option>
                                ))}
                              </select>
                            ) : (
                              <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${style.bg} ${style.border} ${style.text} border`}>
                                {rfq.status}
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-slate-400 text-xs">{formatDate(rfq.createdAt)}</td>
                          <td className="py-3 px-4">
                            {isEditing ? (
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleStatusUpdate(rfq.id, editingStatus)}
                                  disabled={isSaving}
                                  className="flex items-center gap-1 px-2.5 py-1.5 bg-green-500/10 border border-green-500/30 rounded-lg text-green-300 text-xs hover:bg-green-500/20 transition-all disabled:opacity-50"
                                >
                                  <Save className="w-3 h-3" /> Save
                                </button>
                                <button
                                  onClick={() => setEditingId(null)}
                                  className="px-2.5 py-1.5 bg-slate-700 border border-slate-600 rounded-lg text-slate-300 text-xs hover:bg-slate-600 transition-all"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => { setEditingId(rfq.id); setEditingStatus(rfq.status); }}
                                  className="flex items-center gap-1 px-2.5 py-1.5 bg-orange-500/10 border border-orange-500/30 rounded-lg text-orange-300 text-xs hover:bg-orange-500/20 transition-all"
                                >
                                  <Zap className="w-3 h-3" /> Status
                                </button>
                                <button
                                  onClick={() => setExpandedId(expandedId === rfq.id ? null : rfq.id)}
                                  className="flex items-center gap-1 px-2.5 py-1.5 bg-slate-700 border border-slate-600 rounded-lg text-slate-300 text-xs hover:bg-slate-600 transition-all"
                                >
                                  <MessageSquare className="w-3 h-3" /> Details
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                        {/* Expanded Details Row */}
                        {expandedId === rfq.id && (
                          <tr className="bg-slate-800/50">
                            <td colSpan={7} className="p-4">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {/* Assign To */}
                                <div>
                                  <label className="block text-slate-400 text-xs font-semibold uppercase mb-1">Assign To</label>
                                  <div className="flex gap-2">
                                    <input
                                      type="text"
                                      value={assignInput[rfq.id] ?? rfq.assignedTo ?? ''}
                                      onChange={e => setAssignInput(prev => ({ ...prev, [rfq.id]: e.target.value }))}
                                      placeholder="Team member name"
                                      className="flex-1 px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-xs placeholder:text-slate-500 focus:outline-none focus:border-orange-500"
                                    />
                                    <button
                                      onClick={() => handleFieldUpdate(rfq.id, { assignedTo: assignInput[rfq.id] || '' })}
                                      className="px-3 py-2 bg-blue-500/10 border border-blue-500/30 rounded-lg text-blue-300 text-xs hover:bg-blue-500/20"
                                    >
                                      <UserCheck className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                </div>
                                {/* Internal Notes */}
                                <div className="md:col-span-2">
                                  <label className="block text-slate-400 text-xs font-semibold uppercase mb-1">Internal Notes (team only)</label>
                                  <div className="flex gap-2">
                                    <textarea
                                      value={notesInput[rfq.id] ?? rfq.internalNotes ?? ''}
                                      onChange={e => setNotesInput(prev => ({ ...prev, [rfq.id]: e.target.value }))}
                                      placeholder="Add internal notes..."
                                      rows={2}
                                      className="flex-1 px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-xs placeholder:text-slate-500 focus:outline-none focus:border-orange-500 resize-none"
                                    />
                                    <button
                                      onClick={() => handleFieldUpdate(rfq.id, { internalNotes: notesInput[rfq.id] || '' })}
                                      className="px-3 py-2 bg-green-500/10 border border-green-500/30 rounded-lg text-green-300 text-xs hover:bg-green-500/20 self-end"
                                    >
                                      <Save className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                              {/* Form Details */}
                              {(() => {
                                let formDetails: { label: string; value: string }[] = [];
                                try { formDetails = JSON.parse(rfq.formSummary || '[]'); } catch {}
                                let params: Record<string, any> = {};
                                try { params = JSON.parse(rfq.parameters || '{}'); } catch {}
                                return (
                                  <div className="mt-4 pt-4 border-t border-slate-700/50">
                                    <p className="text-slate-400 text-xs font-semibold uppercase mb-2">Submitted Details</p>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                      {formDetails.length > 0
                                        ? formDetails.map((item, i) => (
                                            <div key={i} className="bg-slate-900/50 p-2 rounded-lg">
                                              <p className="text-slate-500 text-[10px] uppercase">{item.label}</p>
                                              <p className="text-white text-xs">{item.value}</p>
                                            </div>
                                          ))
                                        : Object.entries(params).filter(([_, v]) => v).map(([key, val], i) => (
                                            <div key={i} className="bg-slate-900/50 p-2 rounded-lg">
                                              <p className="text-slate-500 text-[10px] uppercase">{key.replace(/([A-Z])/g, ' $1')}</p>
                                              <p className="text-white text-xs">{String(val)}</p>
                                            </div>
                                          ))
                                      }
                                    </div>
                                    {rfq.notes && (
                                      <div className="mt-3 p-3 bg-slate-900/50 rounded-lg">
                                        <p className="text-slate-500 text-[10px] uppercase mb-1">Client Notes</p>
                                        <p className="text-white text-xs">{rfq.notes}</p>
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};
